import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import Api from 'services/api';
import AddAppointment from 'components/appointment/AddAppointment';
import CancelModal from 'components/appointment/CancelModal';
import ViewRequestTable from 'views/appointment/view/ViewRequestTable';
import { SubmitButton } from 'components/common/buttons/SubmitButton';
import toast from 'react-hot-toast';
import FullPageLoader from "components/common/FullPageLoader";

const AppointmentRequestView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  React.useEffect(() => {
    fetchRequestDetails();
  }, [id]);

  const fetchRequestDetails = async () => {
    setLoading(true);
    try {
      const result = await Api.getAppointmentRequest(id);
      if (result?.status === 200 && result?.data?.data) {
        const data = result.data.data;
        setItem({
          ...data,
          appointment_details: data.request_details,
          appointment_custom_pricing: data.request_custom_pricing,
          timezone_id: data.request_details?.timezone_id,
        });
      } else {
        toast.error('Appointment request not found');
        navigate('/appointment-requests');
      }
    } catch (error) {
      toast.error('Error loading appointment request');
      console.error(error);
      navigate('/appointment-requests');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAppointmentForm = () => {
    setShowAppointmentModal(true);
  };

  const handleAppointmentModalClose = () => {
    setShowAppointmentModal(false);
  };

  const handleAppointmentCreated = () => {
    setShowAppointmentModal(false);
    toast.success('Appointment created successfully!');
    // navigate('/user/appointment-requests');
  };

  // if (loading) {
  //   return (
  //     <div className="content">
  //       <div className="loading-container">
  //         <p>Loading appointment request details...</p>
  //       </div>
  //     </div>
  //   );
  // }

  const handleOpenCancel = () => {
    setCancelModal(true);
  };

  const handleCloseCancel = () => {
    setCancelModal(false);
  };

  const handleCancelRequest = async (data) => {
    setLoading(true);
    try {
      const response = await Api.cancelRequest(id, data.reason);
      if (response?.status >= 200 && response?.status < 300) {
        toast.success('Appointment request cancelled successfully!');
        handleCloseCancel();
        fetchRequestDetails();
      } else if (response?.status === 422) {
        toast.error(response.data.message || 'Validation failed');
      } else {
        toast.error(response?.data?.message || 'Failed to cancel appointment request');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error cancelling appointment request');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveToLanguageRequest = async () => {
     setActionLoading(true);
     try {
       const result = await Api.moveToLanguageRequest(item?.id);
       if (result?.status >= 200 && result?.status < 300) {
         toast.success(result.data?.message || 'Moved to language request');
         navigate('/language-requests');
       } else {
         toast.error(result?.data?.message || 'Unable to move request');
       }
     } catch (error) {
       toast.error('Unable to move request');
     }
     setActionLoading(false);
   };

  const AppointmentTimezone = item?.appointment_details?.timezone;
  const rawDatetime = item?.datetime;

  const formattedDate = AppointmentTimezone?.name
      ? moment.tz(rawDatetime, AppointmentTimezone.name).format("MMM D, YYYY")
      : moment(rawDatetime).format("MMM D, YYYY");

  const formattedDefaultTime = AppointmentTimezone?.name
    ? moment.tz(rawDatetime, AppointmentTimezone.name).tz("America/Denver").format("hh:mm A")
    : moment(rawDatetime).format("hh:mm A");

  const formattedClientTime = (() => {
      if (!AppointmentTimezone?.name) return formattedDefaultTime;
      const m = moment.tz(rawDatetime, AppointmentTimezone.name);
      const dynamicAbbr = m.format("z");
      const dynamicOffset = m.format("Z");
      
      const zone = dynamicAbbr ? `(${dynamicAbbr} / GMT${dynamicOffset})` : `(GMT${dynamicOffset})`;
      return `${m.format("hh:mm A")} ${zone}`;
    })();

  return (
    <div className="content">
      {loading && <FullPageLoader />}
      {/* Header with Title */}
      <div className="row_main_head bg-primary-secondaryCard">
        <h2 className="main_heading main_heading1">
          View Appointment Request # {item?.appid || 'N/A'}
        </h2>
        <div className="mb-3">
          <div className="text-transform-capitalize main_heading main_heading1">
            Client: {item?.accounts?.user?.name || 'N/A'}
          </div>
        </div>
      </div>

      {/* Date and Time Info */}
      <div className="row_main_head bg-light-secondaryCard mt-3">
        <h2 className="main_heading2 view-page">
          Date <span className="dateTime viewpage_tags">{formattedDate}</span> /{" "}
          <span className="dateTime viewpage_tags">{formattedDefaultTime}</span> OR{" "}
          <span className="dateTime viewpage_tags">{formattedClientTime}</span>
        </h2>
        <div className="">
          <div className="text-transform-capitalize main_heading2 d-flex align-items-center">
            <span className="appointmentType viewpage_tags">
              {item?.type === 'Telephonic'
              ? 'Telephonic'
              : item?.type || 'N/A'}
            </span>
            <span className="appLanguage viewpage_tags">{item?.language?.name ? item?.language?.name : item?.language_requests?.[0]?.language_name || 'N/A'}</span>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="card_table view_table_custom">
        {/* Action Buttons */}
        <div className="row-primary flex-wrap justify-content-between">
          <div className="row-primary flex-wrap">
            {item?.status !== 'cancelled' && item?.status == 'Request' || item?.status == 'New Request' && (
            <SubmitButton
              buttonType="button"
              className="green"
              btnText="Convert Into Appointment"
              handleSubmit={handleOpenAppointmentForm}
              disabled={actionLoading}
            />
            )}
            {!item?.appointment_id && item?.status !== 'cancelled' && (
              <button className="main_button  tomatoRed" disabled={actionLoading} onClick={handleOpenCancel}>
                Cancel Request
              </button>
            )}
            {item?.status !== 'approved' && item?.status !== 'cancelled' && item?.status !== 'Accepted' && (
              <button
                type="button"
                className="main_button"
                onClick={handleMoveToLanguageRequest}
                disabled={actionLoading}
              >
                {actionLoading ? 'Moving...' : 'Move To Language Request'}
              </button>
            )}
          </div>
        </div>

        {/* Details Table */}
        <ViewRequestTable item={item} isRequestView={true} />
      </div>

      {/* AddAppointment Modal */}
      <AddAppointment
        item={item}
        show={showAppointmentModal}
        onClose={handleAppointmentModalClose}
        callback={handleAppointmentCreated}
        popupType="from-request"
      />

      <CancelModal
        isOpen={cancelModal}
        onClose={handleCloseCancel}
        onConfirm={handleCancelRequest}
        appointmentId={item?.appid || item?.id}
        item={item}
        loader={loading}
        heading="Request"
      />
    </div>
  );
};

export default AppointmentRequestView;
